import { DateInput, DateInputProps } from '@mantine/dates';
import { DateTime } from 'luxon';
import { memo, useMemo } from 'react';
import { RegisterOptions, useController } from 'react-hook-form';
import styled from 'styled-components';

interface Props extends DateInputProps {
	rhfOptions?: RegisterOptions;
	name: string;
	className?: string;
}
// we must avoid creating our wrappers over mantine components but this case is exclusion bcs react-hook-form-mantine
// library dont export DateInput - D.Zghinnik 05.10.2023
const UTCDateInput = ({ name, rhfOptions, className, placeholder, ...props }: Props) => {
	const {
		field,
		fieldState: { error },
	} = useController({ name, rules: rhfOptions });

	const datePickerDefaultLevel = useMemo(() => {
		return field.value ? 'month' : 'decade';
	}, [field.value]);

	const value = useMemo(() => {
		// Input value: dd/mm/yyyy 00:00:00 in UTC (string)
		// Convert to: dd/mm/yyyy 00:00:00 in local time zone (JS Date)
		if (!field.value) {
			return null;
		}
		return DateTime.fromISO(field.value, { zone: 'utc' })
			.setZone('local', { keepLocalTime: true })
			.toJSDate();
	}, [field.value]);

	return (
		<DateInput
			className={className}
			{...field}
			placeholder={placeholder ?? props.label?.toString() ?? 'Select date'}
			error={error?.message}
			value={value}
			dateParser={(input) => {
				// Input value: dd/mm/yyyy (string)
				// Convert to: dd/mm/yyyy 00:00:00 in local time zone (JS Date)
				const [day, month, year] = input.split('/');
				const parsedDate = DateTime.fromFormat(`${year}-${month}-${day}`, 'yyyy-MM-dd', {
					zone: 'local',
				}).toJSDate();
				return parsedDate;
			}}
			onChange={(value) => {
				// Input value: dd/mm/yyyy 00:00:00 in local time zone (JS Date)
				// Convert to: dd/mm/yyyy 00:00:00 in UTC (string)
				if (!value) {
					field.onChange('');
					return;
				}
				const dt = DateTime.fromJSDate(value).setZone('utc', { keepLocalTime: true });
				field.onChange(dt.toISO());
			}}
			defaultLevel={props.defaultLevel || datePickerDefaultLevel}
			popoverProps={{
				withinPortal: true,
				zIndex: 1000,
			}}
			{...props}
		/>
	);
};

const Memoized = memo(UTCDateInput);
const Styled = styled(Memoized)`
	.mantine-Input-input {
		border: ${({ readOnly }) => (readOnly ? 'none' : '0.0625rem solid #D3D8DA')};
		${({ readOnly, leftSection }) =>
			!leftSection && (readOnly ? 'padding-left: 0px;' : 'padding-left: 0.5rem;')}
	}
`;

export { Styled as UTCDateInput };
